import { GlassSolidIcon } from '@ikyucom/design-system-icons-react'
import { CheckBox } from 'macaron-frontend-ui'
import { twMerge } from 'tailwind-merge'
import type { FilterCriteria, FreeFlows } from '.'
import { FilterChip } from './FilterChip'
import { FilterChipsWithPopover, useClose, useCommit, useDraft, useSetDraft } from './Popover'
import { createColor } from './criteria'
import { CheckboxPopup } from './popup'

export type UseSetFreeFlows = () => (freeFlows: FreeFlows) => void

export function FreeFlow({
  useFilterCriteria,
  useFreeFlows,
  useSetFreeFlows,
  useEnabledFreeFlows,
}: {
  useFilterCriteria: () => FilterCriteria | undefined
  useFreeFlows: () => FreeFlows
  useSetFreeFlows: UseSetFreeFlows
  useEnabledFreeFlows: () => FreeFlows
}) {
  const { freeFlow: hasFreeFlow, freeFlowThreeHours: hasFreeFlowThreeHours } = useFreeFlows()
  const setFreeFlows = useSetFreeFlows()
  const criteria = useFilterCriteria()
  const enabled = useEnabledFreeFlows()

  if (!hasFreeFlow && !hasFreeFlowThreeHours) {
    return null
  }

  // 飲み放題と３時間飲み放題がある場合
  if (hasFreeFlow && hasFreeFlowThreeHours) {
    return (
      <FreeFlowsWithPopover
        freeFlow={criteria?.freeFlow ?? false}
        freeFlowThreeHours={criteria?.freeFlowThreeHours ?? false}
        setFreeFlows={setFreeFlows}
        enabled={enabled}
      />
    )
  }

  // ３時間飲み放題のみの場合
  if (!hasFreeFlow && hasFreeFlowThreeHours) {
    const color = createColor({
      enabled: enabled.freeFlowThreeHours ?? false,
      isActive: criteria?.freeFlowThreeHours,
    })
    return (
      <FilterChip
        onClick={() => setFreeFlows({ freeFlowThreeHours: !criteria?.freeFlowThreeHours })}
        color={color}
      >
        <div className="flex items-center gap-1">
          <GlassSolidIcon
            className={twMerge(criteria?.freeFlow ? 'text-marron-light' : 'text-neutral-600')}
          />
          ３時間飲み放題付
        </div>
      </FilterChip>
    )
  }

  // 飲み放題のみの場合
  if (hasFreeFlow && !hasFreeFlowThreeHours) {
    const color = createColor({ enabled: enabled.freeFlow ?? false, isActive: criteria?.freeFlow })
    return (
      <FilterChip onClick={() => setFreeFlows({ freeFlow: !criteria?.freeFlow })} color={color}>
        <div className="flex items-center gap-1">
          <GlassSolidIcon
            className={twMerge(criteria?.freeFlow ? 'text-marron-light' : 'text-neutral-600')}
          />
          飲み放題付
        </div>
      </FilterChip>
    )
  }
  return null
}

type DraftFreeFlow = {
  freeFlow: boolean
  freeFlowThreeHours: boolean
}

function FreeFlowsWithPopover({
  freeFlow,
  freeFlowThreeHours,
  setFreeFlows,
  enabled,
}: {
  freeFlow: boolean
  freeFlowThreeHours: boolean
  setFreeFlows: (freeFlows: FreeFlows) => void
  enabled: FreeFlows
}) {
  const label = createFreeFlowLabel({ freeFlow, freeFlowThreeHours })
  const data: DraftFreeFlow = { freeFlow, freeFlowThreeHours }
  const color = createColor({
    enabled: (enabled.freeFlow ?? false) || (enabled.freeFlowThreeHours ?? false),
    isActive: freeFlow || freeFlowThreeHours,
  })
  return (
    <FilterChipsWithPopover
      trigger={
        <>
          <GlassSolidIcon
            className={color === 'active' ? 'text-marron-light' : 'text-neutral-600'}
          />
          {label}
        </>
      }
      data={data}
      color={color}
    >
      <Body setFreeFlows={setFreeFlows} />
    </FilterChipsWithPopover>
  )
}

function Body({ setFreeFlows }: { setFreeFlows: (freeFlows: FreeFlows) => void }) {
  const draft = useDraft<DraftFreeFlow | undefined>()
  const set = useSetDraft<DraftFreeFlow>()
  const commit = useCommit(setFreeFlows)
  const close = useClose()
  return (
    <CheckboxPopup update={commit} cancel={close}>
      <CheckBox
        checked={draft?.freeFlow ?? false}
        onChange={() => set((prev: DraftFreeFlow) => ({ ...prev, freeFlow: !prev.freeFlow }))}
        label="飲み放題付"
      />
      <div className="px-4">
        <CheckBox
          checked={draft?.freeFlowThreeHours ?? false}
          onChange={() =>
            set((prev: DraftFreeFlow) => ({
              ...prev,
              freeFlowThreeHours: !prev.freeFlowThreeHours,
            }))
          }
          label="3時間飲み放題"
        />
      </div>
    </CheckboxPopup>
  )
}

function createFreeFlowLabel({
  freeFlow,
  freeFlowThreeHours,
}: {
  freeFlow: boolean
  freeFlowThreeHours: boolean
}) {
  if (freeFlow && freeFlowThreeHours) {
    return '飲み放題、他1件'
  }
  if (freeFlow) {
    return '飲み放題付'
  }
  if (freeFlowThreeHours) {
    return '3時間飲み放題'
  }
  return '飲み放題'
}
